// src/pages/lab/processing/components/SampleListTab.tsx
import React, { useState } from "react";
import { Button, Tag, Badge, Input, Space, Typography, Modal, Spin } from "antd";
import { CheckCircleOutlined, UsergroupAddOutlined, CloseCircleOutlined, SearchOutlined, UserSwitchOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { updateSample } from "../../../../api/labiq/SampleApi";
import { updateLabOrderStatus } from "../../../../api/labiq/LabOrdersApi";
import type { ColumnsType } from "antd/es/table";

// Shared components
import { DataTable, PageToolbar, StatusTag } from "../../../../components/shared";

// Components
import SampleModal from "../../tracking/components/SampleModal";
import StartSampleProtocolModal from "./components/StartSampleProtocolModal";

// Types
import { Sample, statusOptions, protocolStatusOptions } from "../../../../types/lab/SampleTypes";
import { Protocol, ProcessingGroup } from "../../../../types/lab/ProtocolTypes";
import { LabOrder } from "../../../../types/lab/LabOrderTypes";

// Utils and hooks
import { useNotifications } from "../../../../context/NotificationContext";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

interface SampleListTabProps {
	samples: Sample[];
	protocols: Protocol[];
	pendingGroups: ProcessingGroup[];
	orders: LabOrder[];
	loading: boolean;
	onRefresh: () => void;
	onAssignToGroup: (groupId: number, samplesToAssign: Sample[]) => Promise<void>;
	assigningToGroup: boolean;
}

const SampleListTab: React.FC<SampleListTabProps> = ({
	samples,
	protocols,
	pendingGroups,
	orders,
	loading,
	onRefresh,
	onAssignToGroup,
	assigningToGroup,
}) => {
	const [selectedSamples, setSelectedSamples] = useState<Sample[]>([]);

	// Modal states
	const [startProtocolModalOpen, setStartProtocolModalOpen] = useState(false);
	const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
	const [assignToGroupModalOpen, setAssignToGroupModalOpen] = useState(false);
	const [samplesToAssign, setSamplesToAssign] = useState<Sample[]>([]);

	// Sample modal states
	const [sampleModalOpen, setSampleModalOpen] = useState(false);

	// Manual complete state
	const [sampleToComplete, setSampleToComplete] = useState<Sample | null>(null);
	const [completingSample, setCompletingSample] = useState(false);
	const PAGE_SIZE = loadUserPreferences().tableRowsPerPage || 25;

	const { showWarning, showSuccess } = useNotifications();

	const handleCompleteSample = (sample: Sample) => {
		setSampleToComplete(sample);
	};

	const confirmCompleteSample = async () => {
		if (!sampleToComplete) return;

		const matchingOrder = orders.find(order =>
			order.order_items?.some(item => item.sample_id === sampleToComplete.sampleID)
		);

		if (!matchingOrder) {
			showWarning("No lab order found for this sample");
			setSampleToComplete(null);
			return;
		}

		setCompletingSample(true);
		try {
			await updateSample(sampleToComplete.id, {
				...sampleToComplete,
				sampleOA: sampleToComplete.sampleOA
					? { ...sampleToComplete.sampleOA, status: "resultsAvailable" }
					: null,
			});

			await updateLabOrderStatus(matchingOrder.id, "resultsAvailable", [
				{ id: matchingOrder.order_items?.find(item => item.sample_id === sampleToComplete.sampleID)?.id || 0, status: "resultsAvailable" }
			]);
			showSuccess("Sample marked as results available");
			setSampleToComplete(null);
			onRefresh();
		} catch (error: any) {
			showWarning(error?.response?.data?.message || "Failed to update sample status");
		} finally {
			setCompletingSample(false);
		}
	};

	const handleStartGroupProtocol = () => {
		if (selectedSamples.length === 0) {
			showWarning("Please select samples to process");
			return;
		}

		setStartProtocolModalOpen(true);
	};

	const handleProtocolStarted = () => {
		setStartProtocolModalOpen(false);
		setSelectedSamples([]);
		onRefresh();
		showSuccess("Protocol started successfully");
	};

	const handleAssignSampleToGroup = (sample: Sample) => {
		setSamplesToAssign([sample]);
		setAssignToGroupModalOpen(true);
	};

	const handleAssignSelectedSamplesToGroup = () => {
		if (selectedSamples.length === 0) {
			showWarning("Please select samples to assign to a group");
			return;
		}
		setSamplesToAssign(selectedSamples);
		setAssignToGroupModalOpen(true);
	};

	const isGroupDisabledForSamples = (group: ProcessingGroup, samplesToCheck: Sample[]): boolean => {
		if (!group.sample_instances) return false;

		const groupSampleIds = new Set(group.sample_instances.map(instance => instance.sample_id));
		return samplesToCheck.some(sample => groupSampleIds.has(sample.sampleID));
	};

	const getGroupDisabledReason = (group: ProcessingGroup, samplesToCheck: Sample[]): string[] => {
		if (!group.sample_instances) return [];

		const groupSampleIds = new Set(group.sample_instances.map(instance => instance.sample_id));
		return samplesToCheck
			.filter(sample => groupSampleIds.has(sample.sampleID))
			.map(sample => sample.sampleID);
	};

	const handleAssignToGroup = async (groupId: number) => {
		try {
			await onAssignToGroup(groupId, samplesToAssign);
			setAssignToGroupModalOpen(false);
			setSamplesToAssign([]);
			setSelectedSamples([]);
		} catch (error: any) {
			// Error handling is done in parent component
		}
	};

	const columns: ColumnsType<Sample> = [
		{
			title: "Sample ID",
			dataIndex: "sampleID",
			key: "sampleID",
			fixed: "left",
			render: (text: string) => text,
		},
		{
			title: "Protocol Status",
			dataIndex: "protocol_status",
			key: "protocol_status",
			render: (status: string) => <StatusTag status={status || "no_protocols"} />,
		},
		{
			title: "Sample Status",
			key: "lab_status",
			render: (_: any, record: Sample) => <StatusTag status={record.sampleOA?.status || "pending"} />,
		},
		{
			title: "Analysis",
			key: "analysis",
			render: (_: any, record: Sample) => {
				const analyses = record.sampleOA?.analysis_requested || [];
				if (analyses.length === 0) return <Text type="secondary">None</Text>;
				return (
					<Space size={4} wrap>
						{analyses.slice(0, 2).map((analysis, index) => (
							<Badge key={index} count={analysis} style={{ backgroundColor: "var(--mg-primary)", fontSize: "11px" }} />
						))}
						{analyses.length > 2 && <Badge count={`+${analyses.length - 2}`} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "11px" }} />}
					</Space>
				);
			},
		},
		{
			title: "Notes",
			key: "notes",
			render: (_: any, record: Sample) => {
				const comments = record.sampleOA?.comments;
				return comments;
			},
		},
		{
			title: "Actions",
			key: "actions",
			fixed: "right",
			render: (_: any, record: Sample) => {
				const isReady = record.protocolInstances && record.protocolInstances?.length > 0 && record.protocolInstances?.every((instance) => instance.status === "completed");

				return (
					<Space size="small">
						<Button type="text" size="small" icon={<UserSwitchOutlined />} onClick={(e) => { e.stopPropagation(); handleAssignSampleToGroup(record); }} disabled={pendingGroups.length === 0} style={{ color: "var(--mg-primary)" }} title="Assign to Group" />

						{isReady && (
							<Button type="text" size="small" icon={<CheckCircleOutlined />} onClick={(e) => { e.stopPropagation(); handleCompleteSample(record); }} disabled={record.protocol_status === "in_progress"} style={{ color: "var(--mg-success)" }} title="Manually Complete Processing" />
						)}
					</Space>
				);
			},
		},
	];


	return (
		<>
			<PageToolbar
				extraActions={[
					{
						label: `Process Group (${selectedSamples.length})`,
						icon: <UsergroupAddOutlined />,
						onClick: () => handleStartGroupProtocol(),
						disabled: selectedSamples.length === 0,
					},
					{
						label: `Assign To Group (${selectedSamples.length})`,
						icon: <UserSwitchOutlined />,
						onClick: handleAssignSelectedSamplesToGroup,
						disabled: selectedSamples.length === 0,
					},
					{
						label: "Clear Selection",
						icon: <CloseCircleOutlined />,
						onClick: () => setSelectedSamples([]),
						disabled: selectedSamples.length === 0,
					}
				]}
			/>
			
			<DataTable
				bare
				columns={columns}
				dataSource={samples}
				rowKey="sampleID"
				loading={loading}
				selectedRows={selectedSamples}
				setSelectedRows={setSelectedSamples}
				onRowClick={(record) => {
					setSelectedSample(record);
					setSampleModalOpen(true);
				}}
				scrollX={1000}
				scrollY="calc(100vh - 305px)"
				pagination={{ pageSize: PAGE_SIZE, }}
				style={{marginTop: '12px'}}
			/>

			{/* Sample Detail Modal */}
			{sampleModalOpen && selectedSample && (
				<SampleModal
					sample={selectedSample}
					isOpen={sampleModalOpen}
					onClose={() => { setSampleModalOpen(false); setSelectedSample(null); }}
					onFormSubmission={() => { }}
					patient={null}
				/>
			)}

			{/* Start Protocol Modal */}
			{startProtocolModalOpen && (
				<StartSampleProtocolModal
					isOpen={startProtocolModalOpen}
					samples={selectedSamples}
					protocols={protocols}
					onClose={() => setStartProtocolModalOpen(false)}
					onProtocolStarted={handleProtocolStarted}
				/>
			)}

			{/* Assign to Group Modal */}
			<Modal
				title="Assign Samples to Processing Group"
				open={assignToGroupModalOpen}
				onCancel={() => setAssignToGroupModalOpen(false)}
				footer={null}
				width={700}
			>
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					<div>
						<Text strong>Samples to assign ({samplesToAssign.length}):</Text>
						<div style={{ marginTop: 8, padding: 8, borderRadius: 4 }}>
							{samplesToAssign.map((sample) => (
								<Tag key={sample.sampleID} style={{ margin: 2 }}>
									{sample.sampleID}
								</Tag>
							))}
						</div>
					</div>

					<div>
						<Text strong>Select Processing Group:</Text>
						{pendingGroups.length === 0 ? (
							<div style={{ textAlign: "center", padding: "40px 0" }}>
								<Text type="secondary">No pending processing groups available</Text>
							</div>
						) : (
							<div style={{ marginTop: 12 }}>
								{pendingGroups.map((group) => {
									const isDisabled = isGroupDisabledForSamples(group, samplesToAssign);
									const disabledSamples = getGroupDisabledReason(group, samplesToAssign);

									return (
										<div
											key={group.id}
											style={{
												marginBottom: 8,
												padding: "12px 16px",
												border: `1px solid var(--mg-border)`,
												borderRadius: 6,
												background: isDisabled ? "var(--mg-bg-subtle)" : "var(--mg-bg-elevated)",
												cursor: isDisabled ? "not-allowed" : "pointer",
												opacity: isDisabled ? 0.6 : 1,
											}}
											onClick={() => { if (!isDisabled) handleAssignToGroup(group.id); }}
										>
											<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
												<div>
													<Text strong style={{ fontSize: "14px", color: isDisabled ? "var(--mg-text-disabled)" : "var(--mg-text)" }}>
														{group.group_name}
													</Text>
													<div style={{ fontSize: "12px", color: "var(--mg-text-secondary)", marginTop: 2 }}>
														Protocol: {group.protocol?.name} v{group.protocol?.version}
													</div>
													{isDisabled && (
														<div style={{ fontSize: "12px", color: "var(--mg-danger)", marginTop: 2 }}>
															Already contains sample(s): {disabledSamples.join(", ")}
														</div>
													)}
												</div>
												<div style={{ textAlign: "right" }}>
													<StatusTag status={isDisabled ? "inactive" : "pending"} label={isDisabled ? "Not Available" : "Pending"} />
													<div style={{ fontSize: "12px", color: "var(--mg-text-secondary)", marginTop: 2 }}>
														{group.sample_count || 0} sample(s)
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</Space>

				{assigningToGroup && (
					<div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
						<Space>
							<Spin size="small" />
							<Text>Assigning samples…</Text>
						</Space>
					</div>
				)}
			</Modal>

			{/* Manual Complete Confirmation Modal */}
			<Modal
				title={<Space><ExclamationCircleOutlined style={{ color: "var(--mg-warning)" }} /><span>Manually Complete Sample</span></Space>}
				open={!!sampleToComplete}
				onCancel={() => setSampleToComplete(null)}
				onOk={confirmCompleteSample}
				okText="Complete Sample"
				okButtonProps={{ loading: completingSample, style: { backgroundColor: "var(--mg-success)" } }}
				cancelButtonProps={{ disabled: completingSample }}
			>
				<Space direction="vertical" size="small" style={{ width: "100%" }}>
					<Text>Are you sure you want to manually mark sample <Text strong>{sampleToComplete?.sampleID}</Text> as complete?</Text>
					<Text type="secondary">This will update the sample lab status to <Text strong>Complete</Text> and mark the associated order item as completed. This action cannot be undone.</Text>
				</Space>
			</Modal>
		</>
	);
};

export default SampleListTab;